import { Button, Checkbox, Label, Link as UiLink } from "@beweegbaas/ui";
import { ParaglideMessage } from "@inlang/paraglide-js-react";
import { createFileRoute, Link, stripSearchParams, useRouter } from "@tanstack/react-router";
import { useServerFn } from "#/modules/util.hooks/use-server-fn";
import { useState } from "react";
import { toast } from "@beweegbaas/ui";
import { z } from "zod/v4";
import { HistoryBackLink } from "#/components/back-link";
import { ErrorBoundaryComponent } from "#/components/error-boundary";
import { FormErrorSummary } from "#/components/form-errors";
import { Page } from "#/components/page";
import { LayoutAuth } from "#/layouts/layout.auth";
import { useCurrentSchool } from "#/modules/school/use-current-school";
import {
  getEndTestPeriodData,
  submitSchoolSubscription,
} from "#/modules/school/subscribe.functions";
import { op } from "#/modules/util.instrumentation/op";
import { nuqsBoolean } from "#/modules/util.pagination/search.nuqs";
import { m } from "#/paraglide/messages";

/** Letterlijke teksten van de checkboxes – worden als consentText naar de API gestuurd. */
const CONSENT_TEXT_BEVOEGDHEID = m["end_test_period.consent_bevoegdheid_text"]();

// The single PDF artifact, generated at website build time and served statically
// (the old portal /nl/tos.pdf route 301s here).
const ALGEMENE_VOORWAARDEN_PDF_URL = "https://beweegbaas.nl/tos.pdf";

type CheckboxName = "bevoegdheid" | "algemeneVoorwaarden";

const endTestPeriodSearchSchema = z.object({
  success: nuqsBoolean(false),
});

/** Defaults stripped from the URL by `stripSearchParams`. */
const END_TEST_PERIOD_SEARCH_DEFAULTS = { success: false };

export const Route = createFileRoute(
  "/$locale/{-$schoolyear}/_authenticated/$school/end-test-period",
)({
  // Opt out of the `$school` DefaultLayout chrome.
  staticData: { layout: false },
  validateSearch: endTestPeriodSearchSchema,
  search: { middlewares: [stripSearchParams(END_TEST_PERIOD_SEARCH_DEFAULTS)] },
  loaderDeps: ({ search }) => ({ success: search.success }),
  loader: ({ deps, params }) =>
    getEndTestPeriodData({
      data: {
        success: deps.success,
        locale: params.locale,
        school: params.school,
        schoolyear: params.schoolyear,
      },
    }),
  component: EndTestPeriod,
  errorComponent: EndTestPeriodError,
});

function EndTestPeriod() {
  const currentSchool = useCurrentSchool();
  const { subscriptionInProgress, price } = Route.useLoaderData();
  const router = useRouter();
  const navigate = Route.useNavigate();
  const submitSubscriptionFn = useServerFn(submitSchoolSubscription);
  const { success: showSuccess } = Route.useSearch();
  const [checked, setChecked] = useState<Record<CheckboxName, boolean>>({
    bevoegdheid: false,
    algemeneVoorwaarden: false,
  });
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setErrorMessage(null);

    const result = await submitSubscriptionFn({
      data: {
        bevoegdheid: checked.bevoegdheid,
        algemeneVoorwaarden: checked.algemeneVoorwaarden,
      },
    });
    if (!result) return;

    if ("formError" in result) {
      setErrorMessage(result.formError ?? null);
      return;
    }

    await router.invalidate();
    // in_progress: verwerkersovereenkomst still pending vs subscription active right away.
    void op.track("subscription_activated", { in_progress: result.subscriptionInProgress });

    if (!result.subscriptionInProgress) {
      toast.success(m["end_test_period.toast_active"]());
      await navigate({
        search: { success: true },
        replace: true,
      });
      return;
    }

    toast.success(m["end_test_period.toast_started"]());
    await navigate({
      search: { success: false },
      replace: true,
    });
  };

  if (subscriptionInProgress) {
    return (
      <LayoutAuth containerClassName="max-w-[60ch]! flex flex-col gap-6 justify-items-start items-start">
        <div className="mb-10">
          <HistoryBackLink fallback={{ to: "/" }} className={""} />
        </div>

        <IconSvgSpinners3DotsBounce className="size-6 text-green-600" />
        <Page.Title className="flex items-center gap-6">
          {m["end_test_period.in_progress_title"]()}
        </Page.Title>
        <div className="prose">
          <p className="mb-6 ">
            <ParaglideMessage
              message={m["end_test_period.in_progress_requested_for"]}
              inputs={{ schoolName: currentSchool.name }}
              markup={{
                strong: ({ children }) => <strong>{children}</strong>,
              }}
            />
          </p>
          <p className=" mb-6">
            <ParaglideMessage
              message={m["end_test_period.in_progress_dva_signed"]}
              markup={{
                em: ({ children }) => <i>{children}</i>,
              }}
            />
          </p>
          <p className="mb-6">{m["end_test_period.in_progress_rights_after_signing"]()}</p>
        </div>
      </LayoutAuth>
    );
  }

  if (showSuccess) {
    return (
      <LayoutAuth containerClassName="max-w-[60ch]! flex flex-col gap-6 justify-items-start items-start">
        <IconLineMdCircleToConfirmCircleTransition className="size-12 text-green-600" />
        <Page.Title className="flex items-center gap-6">
          {m["end_test_period.success_title"]()}
        </Page.Title>
        <div className="prose m-0">
          <p className=" mb-6">{m["end_test_period.success_body"]()}</p>
          <p className="mb-4">{m["end_test_period.success_email_sent"]()}</p>
        </div>

        <div>
          <Button
            variant="secondary"
            nativeButton={false}
            render={
              <Link
                from={Route.fullPath}
                to="/$locale/{-$schoolyear}/$school"
                params={(prev: Record<string, string>) => prev}
              />
            }
          >
            <IconMaterialSymbolsArrowLeftAlt />
            {m["end_test_period.to_dashboard_cta"]()}
          </Button>
        </div>
      </LayoutAuth>
    );
  }

  return (
    <LayoutAuth containerClassName="max-w-[70ch]!">
      <div className="grid justify-items-start gap-6">
        <HistoryBackLink fallback={{ to: "/" }} className={"mb-4"} />
        <Page.Title className="mb-2">{m["end_test_period.title"]()}</Page.Title>
        <div className="prose grid gap-7">
          <p>{m["end_test_period.intro"]()}</p>
          <p className="font-medium">{m["end_test_period.subscription_info_heading"]()}</p>
          <ul className="list-outside list-disc space-y-4 pl-4 *:pl-4">
            <li>
              <ParaglideMessage
                message={m["end_test_period.info_dva_via_kennisnet"]}
                inputs={{ schoolName: currentSchool.name }}
                markup={{
                  strong: ({ children }) => <strong>{children}</strong>,
                }}
              />
            </li>
            <li>{m["end_test_period.info_rights_after_signing"]()}</li>
            <li>{m["end_test_period.info_duration"]()}</li>
            <li>
              {price ? (
                price.discountPrice != null ? (
                  <ParaglideMessage
                    message={m["end_test_period.price_discount"]}
                    inputs={{
                      discountPrice: price.discountPrice.toLocaleString("nl-NL", {
                        minimumFractionDigits: 2,
                      }),
                      price: price.price.toLocaleString("nl-NL", {
                        minimumFractionDigits: 2,
                      }),
                    }}
                  />
                ) : (
                  <ParaglideMessage
                    message={m["end_test_period.price"]}
                    inputs={{
                      price: price.price.toLocaleString("nl-NL", {
                        minimumFractionDigits: 2,
                      }),
                    }}
                  />
                )
              ) : (
                <>{m["end_test_period.price_unavailable"]()}</>
              )}
            </li>
          </ul>
        </div>

        <form onSubmit={onSubmit} className="mt-6 max-w-2xl space-y-6">
          {errorMessage && <FormErrorSummary errors={[errorMessage]} />}

          <div className="space-y-1">
            {[
              {
                name: "bevoegdheid" as const,
                title: m["end_test_period.consent_bevoegdheid_title"](),
                content: CONSENT_TEXT_BEVOEGDHEID,
              },
              {
                name: "algemeneVoorwaarden" as const,
                title: m["end_test_period.consent_terms_title"](),
                content: (
                  <ParaglideMessage
                    message={m["end_test_period.consent_terms_body"]}
                    markup={{
                      link: ({ children }) => (
                        <UiLink
                          href={ALGEMENE_VOORWAARDEN_PDF_URL}
                          target="_blank"
                          rel="noopener noreferrer"
                          variant="secondary"
                          underline={true}
                        >
                          {children}
                        </UiLink>
                      ),
                    }}
                  />
                ),
              },
            ].map(({ name, title, content }) => (
              <div key={name}>
                <Label className="bg-primary/5 has-checked:border-primary has-checked:bg-primary/10 flex cursor-pointer flex-col items-start gap-2 rounded-lg border p-4 transition-colors duration-100">
                  <p className="mb-2 text-sm font-semibold">{title}</p>
                  <div className="flex gap-2">
                    <Checkbox
                      checked={checked[name]}
                      onCheckedChange={(v) =>
                        setChecked((prev) => ({ ...prev, [name]: v === true }))
                      }
                      className="mt-0.5"
                    />
                    <span className="text-sm">{content}</span>
                  </div>
                </Label>
              </div>
            ))}
          </div>

          <div className="flex flex-col gap-4">
            <Button
              type="submit"
              variant="default"
              size="xl"
              disabled={!checked.bevoegdheid || !checked.algemeneVoorwaarden}
            >
              {m["end_test_period.submit"]()}
            </Button>
            <p className="text-muted-foreground place-self-center text-sm">
              {m["end_test_period.multi_school_hint"]()}
            </p>
          </div>
        </form>
      </div>
    </LayoutAuth>
  );
}

function EndTestPeriodError({ error }: { error: unknown }) {
  const params = Route.useParams();
  return <ErrorBoundaryComponent error={error} params={params} />;
}
